import { useMemo } from 'react';
import { useThemeStore } from '../../store/themeStore';

/**
 * Couleurs de graphiques dérivées des tokens du thème actif.
 *
 * Les graphiques Recharts utilisaient des hex codés en dur (ex. la ligne de
 * solde en indigo `#4f46e5`) qui ne suivaient jamais le thème. Ce hook lit les
 * variables CSS appliquées par `themeStore.applyTheme()` et se recalcule à
 * chaque changement de thème (dépendance `themeId`).
 *
 * Conventions sémantiques conservées : revenus = secondary (vert du thème),
 * dépenses = rouge fixe, solde / accent = primary.
 */
export interface ChartColors {
  primary: string;
  secondary: string;
  credit: string; // revenus (tokenisé --color-credit)
  debit: string; // dépenses (tokenisé --color-debit)
  partnerA: string; // axe partenaire A (froid)
  partnerB: string; // axe partenaire B (chaud)
  grid: string;
  axisText: string;
}

export function useChartColors(): ChartColors {
  const themeId = useThemeStore((s) => s.themeId);
  // resolvedMode fait aussi partie des dépendances : le passage clair↔sombre
  // change les variables CSS (crédit/débit/partenaires) sans changer de thème.
  const resolvedMode = useThemeStore((s) => s.resolvedMode);

  return useMemo<ChartColors>(() => {
    const css = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) => {
      const value = css.getPropertyValue(name).trim();
      return value || fallback;
    };

    const secondary = read('--color-secondary-500', '#22c55e');
    const primary = read('--color-primary-600', '#4f46e5');
    return {
      primary,
      secondary,
      credit: read('--color-credit', secondary),
      debit: read('--color-debit', '#ef4444'),
      partnerA: read('--color-partner-a', primary),
      partnerB: read('--color-partner-b', '#C9791F'),
      grid: read('--color-border-default', '#e5e7eb'),
      axisText: read('--color-text-muted', '#6b7280'),
    };
    // themeId + resolvedMode pilotent la recomputation : les variables CSS changent avec eux.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeId, resolvedMode]);
}
